import * as React from "react";
import { Text, Heading, Box, UnorderedList, ListItem } from "@chakra-ui/react";

import MetaHead from "components/MetaHead";
import Container from "components/Chakra/Container";

const Strong: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="span" fontWeight="bold">
    {children}
  </Text>
);

const Italic: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="span" fontStyle="italic">
    {children}
  </Text>
);

const Underline: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="span" textDecoration="underline">
    {children}
  </Text>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box mb="8">
    <Heading as="h3" variant="large_2" mb="4">
      {title}
    </Heading>
    {children}
  </Box>
);

const SianGdpr: React.FC = () => (
  <>
    <MetaHead
      title="Zásady zpracování osobních údajů"
      description="Zásady zpracování osobních údajů společností UlovDomov.cz, Livendo Rekonstrukce a Development a Livendo Investice a Reality."
      canonicalUrl="https://www.ulovdomov.cz/sian"
      noindex
    />
    <Container>
      <Heading as="h1" mt="90px" mb="8">
        Zásady zpracování osobních údajů
      </Heading>

      <Box
        px={{ base: "4", md: "5%" }}
        mb="100px"
        borderColor="ud-grey-200"
        borderWidth="1px"
        borderRadius="8px"
        py="10"
      >
        <Section title="1. Správce osobních údajů">
          <Text mb="4">
            Správcem Vašich osobních údajů je obchodní společnost
          </Text>
          <UnorderedList spacing="3" mb="4">
            <ListItem>
              <Strong>UlovDomov.cz s.r.o.</Strong>, IČO:{" "}
              <Strong>29300631</Strong>, se sídlem Lazaretní 925/9, Zábrdovice,
              615 00 Brno, a to v případě že správcem Vašich osobních údajů byla
              doposud obchodní společnost <Strong>SIAN správa, s.r.o.</Strong>,
              IČO: <Strong>02758105</Strong>, se sídlem Ruská 83/24, Vítkovice,
              703 00 Ostrava;
            </ListItem>
            <ListItem>
              obchodní společnost{" "}
              <Strong>Livendo Rekonstrukce a Development s.r.o.</Strong>, IČO:{" "}
              <Strong>23997567</Strong>, se sídlem Lazaretní 925/9, Zábrdovice,
              615 00 Brno, a to v případě že správcem Vašich osobních údajů byla
              doposud obchodní společnost{" "}
              <Strong>SIAN rekonstrukce, s.r.o.</Strong>, IČO:{" "}
              <Strong>24818267</Strong>, se sídlem Ruská 83/24, Vítkovice, 703
              00 Ostrava; nebo
            </ListItem>
            <ListItem>
              obchodní společnost{" "}
              <Strong>Livendo Investice a Reality s.r.o.</Strong>, IČO:{" "}
              <Strong>24009733</Strong>, se sídlem Lazaretní 925/9, Zábrdovice,
              615 00 Brno, a to v případě že správcem Vašich osobních údajů byla
              doposud obchodní společnost{" "}
              <Strong>Správa investic a nemovitostí s.r.o.</Strong>, IČO:{" "}
              <Strong>24780189</Strong>, se sídlem Ruská 83/24, Vítkovice, 703
              00 Ostrava.
            </ListItem>
          </UnorderedList>
          <Text mb="4" fontStyle="italic">
            <Strong>UlovDomov.cz s.r.o.</Strong>,{" "}
            <Strong>Livendo Rekonstrukce a Development s.r.o.</Strong> a{" "}
            <Strong>Livendo Investice a Reality s.r.o.</Strong> každá vystupuje
            jako samostatný správce osobních údajů ve vztahu k osobním údajům,
            které na ni přešly v rámci právního nástupnictví; pro účely těchto
            zásad jsou dále v textu označovány společně jako{" "}
            <Strong>&bdquo;Správce&ldquo;</Strong>, není-li výslovně uvedeno
            jinak.
          </Text>
          <Text>
            Správce zpracovává Vaše osobní údaje, jakožto subjektu údajů, v
            souladu s{" "}
            <Strong>
              Nařízením Evropského parlamentu a Rady (EU) 2016/679
            </Strong>{" "}
            ze dne 27. dubna 2016, o ochraně fyzických osob v souvislosti se
            zpracováním osobních údajů a o volném pohybu těchto údajů a o
            zrušení směrnice 95/46/ES (obecné nařízení o ochraně osobních údajů)
            (dále jen <Strong>&bdquo;GDPR&ldquo;</Strong>). V následujícím textu
            Vás jakožto <Strong>Správce</Strong> informujeme o způsobu
            zpracování osobních údajů a o Vašich právech s tím souvisejících.
          </Text>
        </Section>

        <Section title="2. Kontaktní údaje Správce">
          <Text mb="4">
            V případě jakýchkoli dotazů, podnětů nebo uplatnění Vašich práv nás
            jakožto <Strong>Správce</Strong> ve všech třech výše uvedených
            situacích můžete kontaktovat na následujících kontaktních údajích:
          </Text>
          <UnorderedList spacing="2" mb="4">
            <ListItem>
              adresa: <Strong>Lazaretní 925/9, Zábrdovice, 615 00 Brno</Strong>,
            </ListItem>
            <ListItem>
              e-mailová adresa: <Strong>maly.jiri@ulovdomov.cz</Strong>,
            </ListItem>
            <ListItem>
              telefon: <Strong>+420 734 651 043</Strong>,
            </ListItem>
            <ListItem>
              nebo datovou schránkou:
              <UnorderedList mt="2" spacing="1">
                <ListItem>
                  ID datové schránky <Strong>UlovDomov.cz s.r.o.</Strong>:{" "}
                  <Strong>8798ebf</Strong>,
                </ListItem>
                <ListItem>
                  ID datové schránky{" "}
                  <Strong>Livendo Rekonstrukce a Development s.r.o.</Strong>:{" "}
                  <Strong>6u43spi</Strong>,
                </ListItem>
                <ListItem>
                  ID datové schránky{" "}
                  <Strong>Livendo Investice a Reality s.r.o.</Strong>:{" "}
                  <Strong>swp3sn9</Strong>.
                </ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
          <Text>
            Subjekt údajů je oprávněn obrátit se na kteréhokoliv z výše
            uvedených správců prostřednictvím uvedených kontaktních údajů.
          </Text>
        </Section>

        <Section title="3. Právní důvody a účely zpracování osobních údajů">
          <Text mb="4">
            Osobní údaje zpracováváme pouze v nezbytném rozsahu a na základě
            některého z následujících právních důvodů dle{" "}
            <Italic>čl. 6 odst. 1 GDPR</Italic>:
          </Text>

          <Text fontWeight="bold" mb="2">
            a) Souhlas subjektu údajů
          </Text>
          <Text mb="4">
            Na základě Vašeho souhlasu se zpracováním svých osobních údajů
            zpracováváme Vaše osobní údaje zejména za účelem zasílání obchodních
            sdělení, newsletterů nebo jiných marketingových sdělení, která
            nesouvisejí přímo se službami nebo zbožím, které jste si od nás
            objednali. Váš souhlas můžete kdykoli vzít zpět.
          </Text>

          <Text fontWeight="bold" mb="2">
            b) Plnění smlouvy
          </Text>
          <Text mb="4">
            V tomto případě je zpracování osobních údajů nezbytné pro vyřízení
            Vaší poptávky zaslané prostřednictvím kontaktního formuláře nebo
            chatové aplikace, objednání našeho zboží nebo služeb, nebo uzavření
            a plnění smlouvy, kterou jsme spolu uzavřeli, včetně dodání
            objednaného zboží nebo služeb.
          </Text>

          <Text fontWeight="bold" mb="2">
            c) Plnění právních povinností
          </Text>
          <Text mb="4">
            Vaše osobní údaje rovněž zpracováváme v případech, kdy je to
            nezbytné pro splnění právních povinností, které se na nás vztahují,
            zejména povinností účetních, daňových a archivačních.
          </Text>

          <Text fontWeight="bold" mb="2">
            d) Oprávněný zájem Správce
          </Text>
          <Text>
            Vaše osobní údaje můžeme zpracovávat také na základě našeho
            oprávněného zájmu, kterým je zejména tzv. přímý marketing nebo
            zasílání dotazníků spokojenosti za situace, kdy takové obchodní
            sdělení souvisí se službou nebo zbožím, které jsme Vám již poskytli.
            Proti tomuto zpracování můžete kdykoliv vznést námitku. V takovém
            případě budou osobní údaje pro tyto účely dále nezpracovávány.
          </Text>
        </Section>

        <Section title="4. Kategorie zpracovávaných osobních údajů">
          <Text>
            Zpracováváme zejména následující osobní údaje:{" "}
            <Strong>identifikační údaje</Strong> (jméno, příjmení, datum
            narození), <Strong>kontaktní údaje</Strong> (e-mailová adresa,
            telefonní číslo), <Strong>adresní údaje</Strong> (bydliště,
            doručovací adresa), další údaje, které nám dobrovolně poskytnete v
            rámci komunikace nebo objednávky.
          </Text>
        </Section>

        <Section title="5. Zdroje osobních údajů">
          <Text>
            Vaše osobní údaje získáváme přímo od Vás, zejména prostřednictvím
            kontaktního formuláře nebo chatové aplikace, při registraci
            uživatelského účtu, při vyplnění objednávky, při vzájemné komunikaci
            e-mailem, telefonicky nebo jiným způsobem.
          </Text>
        </Section>

        <Section title="6. Doba uchovávání osobních údajů">
          <Text>
            Vaše osobní údaje zpracováváme v případě udělení souhlasu s jejich
            zpracováním po dobu platnosti takového souhlasu, nejdéle do jeho
            odvolání. V případě zpracování z jiných důvodů pouze po nezbytnou
            dobu s přihlédnutím ke konkrétnímu důvodu zpracování, zejména po
            dobu trvání smluvního vztahu a následně po dobu vyžadovanou právními
            předpisy. Po uplynutí doby uchovávání jsou osobní údaje vymazány
            nebo anonymizovány.
          </Text>
        </Section>

        <Section title="7. Příjemci osobních údajů">
          <Text>
            Vaše osobní údaje nepředáváme do zemí mimo EU/EHP. Zároveň jsou
            zpřístupněny pouze našim pověřeným zaměstnancům, z nichž jsou
            všichni řádně proškoleni ohledně nakládání s osobními údaji; dále
            našim smluvním partnerům či externím poskytovatelům služeb (právní
            zástupce, účetní, daňový poradce, IT služby). Všichni příjemci jsou
            vázáni povinností mlčenlivosti a dodržují požadavky{" "}
            <Strong>GDPR</Strong>. Zároveň přijímáme a průběžně aktualizujeme
            odpovídající technická a organizační opatření, aby byly Vaše osobní
            údaje chráněny před neoprávněným přístupem, ztrátou, únikem či
            zneužitím.
          </Text>
        </Section>

        <Section title="8. Způsob zpracování a zabezpečení">
          <Text>
            Vaše osobní údaje zpracováváme automatizovaným způsobem (za
            předpokladu, že osobní údaje jsou v elektronické podobě) i
            neautomatizovaným způsobem (v listinné podobě). V této souvislosti
            jsme přijali odpovídající technická a organizační opatření k
            zabezpečení osobních údajů proti ztrátě, zneužití nebo neoprávněnému
            přístupu.
          </Text>
        </Section>

        <Section title="9. Automatizované rozhodování">
          <Text>
            Neprovádíme automatizované individuální rozhodování ani profilování
            ve smyslu <Italic>čl. 22 GDPR</Italic>.
          </Text>
        </Section>

        <Section title="10. Práva subjektu údajů">
          <Text mb="4">
            V souvislosti se zpracováním osobních údajů máte zejména následující
            práva:
          </Text>
          <UnorderedList spacing="3">
            <ListItem>
              <Underline>právo na přístup k osobním údajům</Underline> – máte
              právo od nás požadovat, zda zpracováváme Vaše osobní údaje, a
              pokud ano, jaké konkrétní údaje zpracováváme, za jakým účelem, po
              jakou dobu jsou uchovávány a další informace související s jejich
              zpracováním,
            </ListItem>
            <ListItem>
              <Underline>právo na opravu osobních údajů</Underline> – máte právo
              požadovat, abychom bez zbytečného odkladu opravili nepřesné osobní
              údaje, případně doplnili osobní údaje neúplné,
            </ListItem>
            <ListItem>
              <Underline>právo na výmaz svých osobních údajů</Underline> – máte
              právo požadovat výmaz Vašich osobních údajů, pokud již nejsou
              nezbytné pro účely, pro které byly zpracovávány, nebo pokud
              neexistuje jiný zákonný důvod pro jejich další zpracování,
            </ListItem>
            <ListItem>
              <Underline>právo na omezení zpracování osobních údajů</Underline>{" "}
              – máte právo požadovat, abychom omezili zpracování Vašich osobních
              údajů, a to v těchto případech:
              <UnorderedList mt="2" spacing="1">
                <ListItem>
                  a) zpochybníte přesnost osobních údajů, a to po dobu nezbytnou
                  k ověření jejich správnosti;
                </ListItem>
                <ListItem>
                  b) zpracování je protiprávní a namísto výmazu žádáte omezení
                  jejich použití;
                </ListItem>
                <ListItem>
                  c) osobní údaje již nepotřebujeme pro účely zpracování, avšak
                  Vy je požadujete pro určení, výkon nebo obhajobu právních
                  nároků;
                </ListItem>
                <ListItem>
                  d) vznesete námitku proti zpracování, a to po dobu, než bude
                  ověřeno, zda oprávněné důvody <Strong>Správce</Strong>{" "}
                  převažují nad Vašimi oprávněnými důvody,
                </ListItem>
              </UnorderedList>
            </ListItem>
            <ListItem>
              <Underline>právo na přenositelnost osobních údajů</Underline> –
              máte právo získat osobní údaje, které jste nám poskytli, ve
              strukturovaném, běžně používaném a strojově čitelném formátu, a
              zároveň právo tyto údaje předat jinému správci,
            </ListItem>
            <ListItem>
              <Underline>
                právo vznést námitku proti zpracování osobních údajů
              </Underline>{" "}
              – máte právo vznést námitku proti zpracování Vašich osobních
              údajů; pokud je námitka vznesena proti zpracování pro účely
              přímého marketingu, nebudou osobní údaje pro tyto účely dále
              zpracovávány,
            </ListItem>
            <ListItem>
              <Underline>
                právo na odvolání souhlasu se zpracováním osobních údajů
              </Underline>{" "}
              – pokud je zpracování založeno na Vašem souhlasu, máte právo tento
              souhlas kdykoliv odvolat, aniž by tím byla dotčena zákonnost
              zpracování před jeho odvoláním.
            </ListItem>
          </UnorderedList>
        </Section>

        <Section title="11. Uplatnění práv a dozorový úřad">
          <Text>
            Svých práv se můžete domáhat prostřednictvím našich kontaktních
            údajů uvedených výše. Jakémukoli Vašemu podnětu se budeme věnovat s
            nejvyšší prioritou. Pokud byste se přesto domnívali, že z naší
            strany dochází k porušení Vašich práv týkajících se ochrany osobních
            údajů, máte právo podat stížnost u{" "}
            <Strong>Úřadu pro ochranu osobních údajů, www.uoou.cz</Strong>.
          </Text>
        </Section>
      </Box>
    </Container>
  </>
);

export default SianGdpr;
